import { useState } from 'react'
import { gql } from 'graphql-request'
import { useParams, Routes, Route, Link } from 'react-router-dom'
import {
  Home as HomeIcon,
  ArrowUpSquareIcon,
  Settings2 as SettingsIcon,
  MoreVertical as MoreVerticalIcon
} from 'lucide-react'

import { useQuery } from '@hooks/graphql'
import { useBreakpoint } from '@contexts/breakpoints'
import { Button } from '@components/ui/button'
import Breadcrumbs from '@components/Breadcrumbs'
import NavTabs from '@components/NavTabs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@components/ui/dropdown-menu'

import Overview from './Overview'
import Settings from './Settings'
import AssignModal from './AssignModal'

const SERIES_QUERY = gql`
  query series($id: ID!) {
    node(id: $id) {
      ... on Series {
        id
        name
        description
        notes
        visibility
        totalPossibleScore
        coverImage {
          url
        }
        classrooms {
          id
          name
        }
        profile {
          id
          fullName
        }
        organization {
          id
          name
        }
        educatorProjects {
          id
          name
          playground
          position
          totalPossibleScore
        }
      }
    }
  }
`

const SeriesDetails = () => {
  const { id } = useParams()
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
  const isMobile = useBreakpoint('sm')

  const { isLoading, refetch, data: { node: series = {} } = {} } = useQuery({
    queryKey: ['seriesDetails', id],
    gqlQuery: SERIES_QUERY,
    variables: { id }
  })

  if (isLoading) return

  return (
    <div className='w-full'>
      <Breadcrumbs>
        <Breadcrumbs.Link label='Pathways' to='/educators/courses' />
        <Breadcrumbs.Text label={series.name} />
        {isMobile ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="ml-auto h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreVerticalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setIsAssignModalOpen(true)}>
                <span className="flex items-center gap-2">
                  <ArrowUpSquareIcon className="h-4 w-4" />
                  Assign
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            onClick={() => setIsAssignModalOpen(true)}
            theme='primary'
            className='ml-auto'
          >
            <span className='flex gap-2 items-center'>Assign<ArrowUpSquareIcon className='h-6 w-6' /></span>
          </Button>
        )}
      </Breadcrumbs>

      <NavTabs className='mx-5'>
        <NavTabs.Link
          to=''
          end
          icon={<HomeIcon size={16} />}
          label='Overview'
        />
        <NavTabs.Link
          to='settings'
          label='Settings'
          icon={<SettingsIcon size={16} />}
        />


      </NavTabs>

      <div className='w-full pb-8'>
        <Routes>
          <Route
            index
            element={
              <Overview
                id={series.id}
                name={series.name}
                description={series.description}
                notes={series.notes}
                visibility={series.visibility}
                educatorProjects={series.educatorProjects}
                classrooms={series.classrooms}
                imageUrl={series.coverImage?.url}
                educatorName={series.profile?.fullName}
                organizationName={series.organization?.name}
                projectCount={series.educatorProjects?.length}
                refetch={refetch}
              />
            }
          />

          <Route
            path='settings'
            element={
              <Settings
                id={series.id}
                refetch={refetch}
                name={series.name}
                description={series.description}
                visibility={series.visibility}
                notes={series.notes}
                imageUrl={series.coverImage?.url}
              />
            }
          />
        </Routes>
      </div>

      <AssignModal
        id={id}
        isOpen={isAssignModalOpen}
        closeModal={() => setIsAssignModalOpen(false)}
        refetch={refetch}
        classrooms={series.classrooms}
        name={series.name}
      />
    </div>
  )
}

export default SeriesDetails
